class JlCategoryRail extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.viewport = this.querySelector('[data-rail-viewport]');
    this.controls = this.querySelector('[data-rail-controls]');
    this.previous = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.status = this.querySelector('[data-rail-status]');
    if (!this.viewport || !this.controls) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.previous.addEventListener('click', () => this.go(this.metrics().page - 1), { signal });
    this.next.addEventListener('click', () => this.go(this.metrics().page + 1), { signal });
    this.viewport.addEventListener('scroll', () => this.refresh(), { signal, passive: true });
    this.viewport.addEventListener('keydown', event => {
      if (event.target !== this.viewport) return;
      const m = this.metrics();
      const targets = { ArrowLeft: m.page - 1, ArrowRight: m.page + 1, Home: 0, End: m.pages - 1 };
      if (!(event.key in targets)) return;
      event.preventDefault();
      this.go(targets[event.key]);
    }, { signal });
    this.viewport.addEventListener('dragstart', event => event.preventDefault(), { signal });
    this.viewport.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.metrics().max <= 1) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, active: false };
    }, { signal });
    window.addEventListener('pointermove', event => {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 6) { this.drag = null; return; }
        if (Math.abs(dx) < 6) return;
        drag.active = true;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture?.(event.pointerId);
      }
      event.preventDefault();
      this.viewport.scrollLeft = drag.left - dx;
      this.refresh();
    }, { signal, passive: false });
    const finish = event => {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      this.drag = null;
      this.viewport.classList.remove('is-dragging');
      if (this.viewport.hasPointerCapture?.(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      if (drag.active) {
        this.suppressUntil = performance.now() + 350;
        const m = this.metrics();
        this.go(m.page, true);
      }
    };
    window.addEventListener('pointerup', finish, { signal });
    window.addEventListener('pointercancel', finish, { signal });
    this.viewport.addEventListener('click', event => {
      if (performance.now() >= (this.suppressUntil || 0)) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressUntil = 0;
    }, { signal, capture: true });
    document.addEventListener('shopify:block:select', event => {
      const card = event.target.closest?.('.pw-categories__card');
      if (card && this.viewport.contains(card)) card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, { signal });
    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this.viewport);
    this.mutationObserver = new MutationObserver(() => this.refresh());
    this.mutationObserver.observe(this.viewport, { childList: true });
    this.refresh();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.drag = null;
    this.viewport?.classList.remove('is-dragging');
  }

  metrics() {
    const width = this.viewport.clientWidth;
    const max = Math.max(0, this.viewport.scrollWidth - width);
    const first = this.viewport.querySelector('.pw-categories__card');
    const gap = parseFloat(getComputedStyle(this.viewport).columnGap) || 0;
    const pitch = Math.max(1, (first?.getBoundingClientRect().width || width) + gap);
    const pages = max <= 1 ? 1 : Math.ceil((max - 1) / pitch) + 1;
    const left = Math.max(0, Math.min(max, this.viewport.scrollLeft));
    const page = max > 1 && left >= max - 1 ? pages - 1 : Math.min(pages - 1, Math.round(left / pitch));
    return { max, pitch, pages, left, page };
  }

  go(page, immediate = false) {
    const m = this.metrics();
    const target = Math.min(m.max, Math.max(0, Math.min(m.pages - 1, page)) * m.pitch);
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.viewport.scrollTo({ left: target, behavior: immediate || reduced ? 'instant' : 'smooth' });
    this.refresh();
  }

  refresh() {
    const m = this.metrics();
    this.controls.hidden = m.pages < 2;
    this.previous.disabled = m.left <= 1;
    this.next.disabled = m.left >= m.max - 1;
    const text = (m.page + 1) + ' / ' + m.pages;
    if (this.status.textContent !== text) this.status.textContent = text;
    this.status.setAttribute('aria-label', this.dataset.language?.startsWith('zh')
      ? '第 ' + (m.page + 1) + ' 页，共 ' + m.pages + ' 页'
      : 'Page ' + (m.page + 1) + ' of ' + m.pages);
  }
}
if (!customElements.get('jl-category-rail')) customElements.define('jl-category-rail', JlCategoryRail);
